// src/components/MainLayout.js

import React, { useState } from 'react';
import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { Box, Drawer, AppBar, Toolbar, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme, Divider, Avatar, IconButton, useMediaQuery, Collapse } from '@mui/material';
import logo from '../assets/logo.png';
import digitalpediLogo from '../assets/digitalpedi-logo.png';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import GarageIcon from '@mui/icons-material/Garage';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import NotificationBell from './NotificationBell';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Tooltip from '@mui/material/Tooltip';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const drawerWidth = 280;

// D3 / Faz 6: Dahili garanti modülü emekliye ayrıldı; menü artık harici
// Solar Gard garanti sayfasını yeni sekmede açar.
const HARICI_GARANTI_URL = 'https://garanti.solargard.com.tr';

const MainLayout = () => {
    const { currentUser, finansGizli, toggleFinansGizli } = useAuth();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [musteriMenuOpen, setMusteriMenuOpen] = useState(false);

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
    const handleLogout = () => auth.signOut();

    const handleMusteriMenuClick = () => {
        setMusteriMenuOpen(!musteriMenuOpen);
    };

    const allMenuItems = [
        { text: 'Ana Sayfa', path: '/dashboard', icon: <DashboardIcon /> },
        { text: 'Garaj', path: '/garaj', icon: <GarageIcon /> },
        { text: 'Ön Görüşme', path: '/on-gorusme', icon: <SupportAgentIcon /> },
        { text: 'Randevu ve İş Takvimi', path: '/randevular', icon: <EventAvailableIcon />, exact: true },
        { text: 'Randevu Listesi', path: '/randevular/liste', icon: <FormatListBulletedIcon /> },
        { text: 'İş Emirleri', path: '/is-emirleri', icon: <ListAltIcon /> },
        { text: 'Garantiler', external: true, url: HARICI_GARANTI_URL, icon: <VerifiedUserIcon /> },
        { text: 'Raporlar', path: '/raporlar', icon: <AssessmentIcon /> },
        {
            text: 'Müşteri Yönetimi',
            icon: <GroupWorkIcon />,
            id: 'musteri',
            subItems: [
                { text: 'Müşteriler', path: '/musteriler', icon: <PeopleIcon /> },
                { text: 'Araçlar', path: '/araclar', icon: <DirectionsCarIcon /> },
                { text: 'Cari Hesaplar', path: '/cari-hesaplar', icon: <AccountBalanceWalletIcon /> },
            ]
        },
        { text: 'Ayarlar', path: '/ayarlar', icon: <SettingsIcon />, roles: ['Genel Müdür', 'Müdür'] },
    ];

    const menuItems = allMenuItems.filter(item => {
        if (!currentUser?.role) return false;
        if (item.roles && !item.roles.includes(currentUser.role)) return false;
        if (currentUser.role === 'Usta') return item.path === '/is-emirleri';
        if (currentUser.role === 'Koordinatör') return item.path !== '/raporlar';
        return true;
    });

    const isMusteriMenuActive = location.pathname.startsWith('/musteri') || location.pathname.startsWith('/arac') || location.pathname.startsWith('/cari-hesap');

    React.useEffect(() => {
        if (isMusteriMenuActive && !musteriMenuOpen) {
            setMusteriMenuOpen(true);
        }
    }, [isMusteriMenuActive, musteriMenuOpen]);

    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'center', p: 2, height: '80px' }}>
                <Link to="/dashboard">
                    <img src={logo} alt="Detailing Dükkanı 216 Logo" style={{ maxHeight: '55px', display: 'block' }} />
                </Link>
            </Toolbar>
            <Divider />
            <List sx={{ p: 1, flexGrow: 1, overflowY: 'auto' }}>
                {menuItems.map((item) => {
                    if (item.subItems) {
                        return (
                            <React.Fragment key={item.text}>
                                <ListItemButton onClick={handleMusteriMenuClick} sx={{ borderRadius: 2, margin: '4px 0' }}>
                                    <ListItemIcon sx={{ minWidth: '40px' }}>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.text} />
                                    {musteriMenuOpen ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>
                                <Collapse in={musteriMenuOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding sx={{ pl: 2 }}>
                                        {item.subItems.map((sub) => (
                                            <ListItem key={sub.text} disablePadding>
                                                <ListItemButton component={NavLink} to={sub.path} onClick={isMobile ? handleDrawerToggle : undefined}
                                                    selected={location.pathname.startsWith(sub.path)}
                                                    sx={{ '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: theme.palette.action.selected, color: theme.palette.primary.main, '& .MuiListItemIcon-root': { color: theme.palette.primary.main } }, '&:hover': { backgroundColor: theme.palette.action.hover }, borderRadius: 2, margin: '4px 0' }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: '40px' }}>{sub.icon}</ListItemIcon>
                                                    <ListItemText primary={sub.text} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        );
                    }

                    // Harici bağlantı (garanti sayfası): rota değil, yeni sekme.
                    if (item.external) {
                        return (
                            <ListItem key={item.text} disablePadding>
                                <ListItemButton
                                    component="a"
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={isMobile ? handleDrawerToggle : undefined}
                                    sx={{ '&:hover': { backgroundColor: theme.palette.action.hover }, borderRadius: 2, margin: '4px 0' }}
                                >
                                    <ListItemIcon sx={{ minWidth: '40px' }}>{item.icon}</ListItemIcon>
                                    <ListItemText primary={item.text} secondary="Harici sayfa" />
                                    <OpenInNewIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                                </ListItemButton>
                            </ListItem>
                        );
                    }

                    return (
                        <ListItem key={item.text} disablePadding>
                            <ListItemButton component={NavLink} to={item.path} onClick={isMobile ? handleDrawerToggle : undefined}
                                selected={item.exact
                                    ? location.pathname === item.path
                                    : (location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path)))}
                                sx={{ '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: theme.palette.action.selected, color: theme.palette.primary.main, '& .MuiListItemIcon-root': { color: theme.palette.primary.main } }, '&:hover': { backgroundColor: theme.palette.action.hover }, borderRadius: 2, margin: '4px 0' }}
                            >
                                <ListItemIcon sx={{ minWidth: '40px' }}>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
            <Divider />
            {/* Kullanıcı Bilgi Alanı - Menü Altında */}
            {currentUser && (
                <Box sx={{ p: 2 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                        backgroundColor: 'rgba(0, 122, 255, 0.08)',
                        borderRadius: 3,
                        mb: 1.5
                    }}>
                        <Avatar sx={{
                            bgcolor: 'primary.main',
                            width: 40,
                            height: 40,
                            fontSize: '1rem',
                            background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)'
                        }}>
                            {currentUser.email?.substring(0, 1).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="600" noWrap>
                                {currentUser.username || currentUser.email?.split('@')[0]}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {currentUser.role || 'Kullanıcı'}
                            </Typography>
                        </Box>
                        <Tooltip title={finansGizli ? "Finansalları Göster" : "Finansalları Gizle"}>
                            <IconButton onClick={toggleFinansGizli} size="small" sx={{ color: finansGizli ? 'warning.main' : 'text.secondary' }}>
                                {finansGizli ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <NotificationBell />
                    </Box>
                    <Box
                        onClick={handleLogout}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            p: 1.5,
                            backgroundColor: 'rgba(255, 59, 48, 0.08)',
                            borderRadius: 2,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 59, 48, 0.15)',
                            }
                        }}
                    >
                        <Typography variant="body2" fontWeight="500" color="error.main">
                            Çıkış Yap
                        </Typography>
                    </Box>
                </Box>
            )}
            <Divider />
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <img src={digitalpediLogo} alt="Digitalpedi Logo" style={{ maxHeight: '20px', marginBottom: '8px' }} />
                <Typography variant="caption" display="block" color="text.secondary">
                    Bu yazılım <a href="https://www.digitalpedi.com" target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 'bold' }}>Digitalpedi</a> tarafından yapılmıştır.
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    <a href="mailto:hello@digitalpedi.com" style={{ color: 'inherit', textDecoration: 'none' }}>hello@digitalpedi.com</a>
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                    Tel: 0530 687 81 09
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: { lg: 'flex' }, minHeight: '100vh', p: { xs: 1, sm: 2 }, gap: { xs: 1, sm: 2 }, bgcolor: 'background.default' }}>
            {isMobile && (
                <AppBar position="fixed" sx={{ borderRadius: 0, border: 'none', boxShadow: 'none' }}>
                    <Toolbar sx={{ backgroundColor: 'background.paper', borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, color: 'text.primary' }}>
                            <MenuIcon />
                        </IconButton>
                        <Link to="/dashboard" style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={logo} alt="Detailing Dükkanı 216 Logo" style={{ maxHeight: '40px', objectFit: 'contain' }} />
                        </Link>
                        {/* Sağ tarafta boşluk için */}
                        <Box sx={{ width: 48 }} />
                    </Toolbar>
                    <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }}
                        sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundColor: '#ffffff' } }}
                    >
                        {drawerContent}
                    </Drawer>
                </AppBar>
            )}

            {!isMobile && (
                <Drawer variant="permanent" sx={{ width: drawerWidth, flexShrink: 0, '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', backgroundColor: 'background.paper', color: 'text.primary', border: 'none', borderRadius: 3, position: 'relative', height: 'auto' } }}>
                    {drawerContent}
                </Drawer>
            )}

            <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 } }}>
                <Box sx={{ flexGrow: 1, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, p: { xs: 2, sm: 3 }, bgcolor: 'background.paper', mt: { xs: '64px', lg: 0 } }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
};

export default MainLayout;